import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

// Sample testimonials data - using product images instead of person photos
const TESTIMONIALS = [
  {
    id: 1,
    name: "Priya Sharma",
    location: "Delhi",
    comment: "The nebulizer quality is exceptional. My patients have given great feedback about its low noise operation. The delivery was prompt and the product exceeded my expectations.",
    product: "Compressor Nebulizer",
    rating: 5,
    image: "https://customer-assets.emergentagent.com/job_init-point/artifacts/5c06fm9w_M2.jpeg"
  },
  {
    id: 2,
    name: "Rajesh Kumar",
    location: "Mumbai",
    comment: "Purchased the BP Monitor for my father. Very accurate readings and the rechargeable feature is a huge plus. Customer service was very helpful in guiding the usage.",
    product: "Blood Pressure Monitor",
    rating: 5,
    image: "https://customer-assets.emergentagent.com/job_init-point/artifacts/vwa15qwx_S2.jpeg"
  },
  {
    id: 3,
    name: "Anjali Verma",
    location: "Bangalore",
    comment: "The steamer vaporizer has been a lifesaver during the winter season. Easy to use and very effective. Highly recommend Alaxico for quality medical equipment.",
    product: "3-in-1 Steamer Vaporizer",
    rating: 5,
    image: "https://customer-assets.emergentagent.com/job_init-point/artifacts/viy436f8_N1.jpeg"
  },
  {
    id: 4,
    name: "Amit Patel",
    location: "Ahmedabad",
    comment: "As a clinic owner, I've ordered surgical caps in bulk. The quality is premium and pricing is very competitive. Will definitely continue ordering from Alaxico.",
    product: "Surgeon Cap Pack",
    rating: 5,
    image: "https://customer-assets.emergentagent.com/job_init-point/artifacts/98ycmb6i_P2.jpeg"
  },
  {
    id: 5,
    name: "Sunita Reddy",
    location: "Hyderabad",
    comment: "The hot water bottle is perfect for pain relief. Great quality rubber and keeps water hot for hours. Very satisfied with my purchase!",
    product: "Hot Water Bottle",
    rating: 5,
    image: "https://customer-assets.emergentagent.com/job_init-point/artifacts/ucwdjm1t_V2.jpeg"
  }
];

const CustomerTestimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % TESTIMONIALS.length);
  };

  const currentTestimonial = TESTIMONIALS[currentIndex];

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-white to-purple-50" data-testid="customer-testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-gray-900 mb-4">
            What our <span className="text-purple-600">customers say</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-500 max-w-2xl mx-auto">
            Trusted by healthcare professionals and families across India
          </p>
        </div>

        {/* Main Testimonial Card */}
        <div className="relative max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden">
            <div className="flex flex-col lg:flex-row">
              {/* Image Section - Product Image */}
              <div className="lg:w-1/2 relative bg-gradient-to-br from-purple-100 to-purple-50 flex items-center justify-center p-8">
                <div className="aspect-square w-full max-w-[350px]">
                  <img
                    src={currentTestimonial.image}
                    alt={currentTestimonial.product}
                    className="w-full h-full object-contain drop-shadow-xl"
                  />
                </div>
              </div>

              {/* Content Section */}
              <div className="lg:w-1/2 relative">
                {/* Purple accent border */}
                <div className="absolute top-0 right-0 w-full h-2 lg:w-2 lg:h-full bg-purple-600" />
                
                <div className="p-8 sm:p-10 lg:p-12 flex flex-col justify-center min-h-[300px] lg:min-h-[450px]">
                  {/* Large Quote Icon */}
                  <div className="mb-6">
                    <Quote className="w-12 h-12 sm:w-16 sm:h-16 text-purple-200 fill-purple-200" />
                  </div>

                  {/* Testimonial Text */}
                  <blockquote className="text-lg sm:text-xl lg:text-2xl text-gray-700 leading-relaxed mb-8 italic">
                    &ldquo;{currentTestimonial.comment}&rdquo;
                  </blockquote>

                  {/* Customer Info */}
                  <div className="mt-auto">
                    <p className="text-xl sm:text-2xl font-semibold text-gray-900">
                      {currentTestimonial.name}
                    </p>
                    <p className="text-base sm:text-lg text-gray-500">
                      From {currentTestimonial.location}
                    </p>
                    {currentTestimonial.product && (
                      <p className="text-sm text-purple-600 mt-2">
                        Purchased: {currentTestimonial.product}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-center mt-8 gap-4">
            <button
              onClick={goToPrevious}
              className="p-3 rounded-full bg-white shadow-lg hover:bg-purple-50 transition-colors border border-gray-200"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-6 h-6 text-purple-600" />
            </button>

            {/* Dots Indicator */}
            <div className="flex items-center gap-2">
              {TESTIMONIALS.map((testimonial, index) => (
                <button
                  key={testimonial.id}
                  onClick={() => {
                    setIsAutoPlaying(false);
                    setCurrentIndex(index);
                  }}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-purple-600 w-8' 
                      : 'bg-gray-300 hover:bg-purple-300'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={goToNext}
              className="p-3 rounded-full bg-white shadow-lg hover:bg-purple-50 transition-colors border border-gray-200"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-6 h-6 text-purple-600" />
            </button>
          </div>
        </div>

        {/* Additional Testimonial Cards (smaller) */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.filter((_, i) => i !== currentIndex).slice(0, 3).map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-purple-50 rounded-2xl p-6 sm:p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              {/* Quote Icon */}
              <Quote className="w-8 h-8 text-purple-300 fill-purple-300 mb-4" />
              
              {/* Name and Location at Top */}
              <div className="mb-4">
                <p className="text-lg font-semibold text-gray-900">{testimonial.name}</p>
                <p className="text-sm text-gray-500">From {testimonial.location}</p>
              </div>

              {/* Comment */}
              <p className="text-gray-600 leading-relaxed line-clamp-4 mb-4">
                {testimonial.comment}
              </p>

              {/* Closing Quote */}
              <div className="flex justify-end">
                <Quote className="w-8 h-8 text-purple-300 fill-purple-300 rotate-180" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CustomerTestimonials;
